/**
 * Utility functions for @mention system
 * Table ID 70648: Users Profile
 * Table ID 70654: Mentions
 */

const USERS_TABLE_ID = 70648;
const MENTIONS_TABLE_ID = 70654;

/**
 * Extract unique usernames from text
 * Supports both @username and @[Full Name] formats
 * @param {string} text - Text containing @mentions
 * @returns {string[]} - Array of unique usernames
 */
export function extractMentionNames(text) {
  if (!text) return [];

  // Match @[Full Name] (preferred for names with spaces) or @username
  const mentionRegex = /@\[([^\]]+)\]|@(\w+)/g;
  const matches = [];
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // match[1] is the content of @[...], match[2] is the content of @...
    const username = (match[1] || match[2]).trim();
    if (username) {
      matches.push(username);
    }
  }

  // Return unique names only
  return [...new Set(matches)];
}

/**
 * Validate and fetch user data for mentioned usernames
 * @param {string[]} usernames - Array of usernames to validate
 * @returns {Promise<Array>} - Array of valid user objects
 */
export async function validateMentionedUsers(usernames) {
  if (!usernames || usernames.length === 0) return [];

  const validUsers = [];

  for (const username of usernames) {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(USERS_TABLE_ID, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'name', op: 'Equal', value: username }]
      });

      if (!error && data?.List?.[0]) {
        validUsers.push(data.List[0]);
      }
    } catch (e) {
      console.warn(`[Mentions] Failed to validate user: ${username}`, e);
    }
  }

  return validUsers;
}

/**
 * Store mentions in database (Table 70654)
 * @param {Array} validUsers - Array of valid user objects
 * @param {number} mentionerId - ID of user making the mention
 * @param {string} entityType - Type: 'comment', 'reply', 'topic'
 * @param {number} entityId - ID of the entity
 */
export async function storeMentions(validUsers, mentionerId, entityType, entityId) {
  if (!validUsers || validUsers.length === 0 || !entityId) return;

  const now = new Date().toISOString();
  const mentionerIdNum = Number(mentionerId);
  const entityIdNum = Number(entityId);

  for (const user of validUsers) {
    try {
      const mentionedUserId = Number(user.id);

      // Check if mention already exists to avoid duplicates
      const { data: existing } = await window.ezsite.apis.tablePage(MENTIONS_TABLE_ID, {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        { name: 'mentioned_user_id', op: 'Equal', value: mentionedUserId },
        { name: 'entity_type', op: 'Equal', value: entityType },
        { name: 'entity_id', op: 'Equal', value: entityIdNum }]

      });

      if (existing?.List?.length > 0) {
        continue;
      }

      await window.ezsite.apis.tableCreate(MENTIONS_TABLE_ID, {
        mentioned_user_id: mentionedUserId,
        mentioner_id: mentionerIdNum,
        entity_type: entityType,
        entity_id: entityIdNum,
        created_at: now
      });
    } catch (e) {
      console.error(`[Mentions] Error storing mention for user ${user.id}:`, e);
    }
  }
}

/**
 * Parse text for display with highlighted mentions
 * Supports both @username and @[Full Name] formats
 * @param {string} text - Text containing @mentions
 * @returns {Array} - Array of text segments and mention objects
 */
export function parseMentionsForDisplay(text) {
  if (!text) return [];

  // Regex to find mentions: @[Full Name] or @username
  // group 1: @[Full Name] content, group 2: @username content
  const mentionRegex = /@\[([^\]]+)\]|@(\w+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    const fullMatch = match[0];
    const username = match[1] || match[2];
    const matchIndex = match.index;
    const displayContent = '@' + username;

    // Add text before mention
    if (matchIndex > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, matchIndex)
      });
    }

    // Add mention
    parts.push({
      type: 'mention',
      content: displayContent,
      username: username,
      raw: fullMatch
    });

    lastIndex = matchIndex + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }];
}